import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Infinity as InfinityIcon, X, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useDashboard } from '@/context/DashboardContext';
import { dashboardApi } from '@/services/dashboardApi';
import { LockedChannelNote } from '@/components/dashboard/BillingChannelPicker';

/*
  Upgrade a plaza vitalicia (solo PRO y Premium) — regla de Samuel, 06/10/2026:
    - Al pagar la anual aparece un pop-up: "¿Quieres hacer upgrade a vitalicio por 500 €?"
    - Si se cierra, la oferta se pierde y el precio pasa a 1.000 € (30 días desde la compra).
  El backend decide precio y vigencia (membership.lifetime_offer); aquí solo se muestra.
  Se cobra por la misma vía (particular / empresa con IVA) que la anual que sustituye: no se elige.
*/

const PLAN_NAME = { basica: 'PRO', premium: 'Premium' };
// Importe en la moneda de la región del cliente (EEE: EUR · resto: USD). Lo decide el backend.
const group = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const money = (cents, currency) => (currency === 'usd' ? `${group(cents / 100)} $` : `${group(cents / 100)} €`);
// '' si la fecha falta o no es válida (toLocaleDateString no lanza: devolvería «Invalid Date»)
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
};

async function goToCheckout(setLoading) {
  setLoading(true);
  try {
    const { checkout_url } = await dashboardApi.createLifetimeCheckout();
    window.location.href = checkout_url;
  } catch (err) {
    toast.error(err.message || 'No se pudo iniciar el pago');
    setLoading(false);
  }
}

export function LifetimeUpgradeModal() {
  const { membership, refreshMembership } = useDashboard();
  const offer = membership?.lifetime_offer;
  const visible = !!offer?.show_popup;
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);

  const decline = async () => {
    if (closing || loading) return;
    setClosing(true);
    try { await dashboardApi.declineLifetimeOffer(); } catch { /* el backend la caduca igualmente */ }
    await refreshMembership();
    setClosing(false);
  };

  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') decline(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <AnimatePresence>
      {visible && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          role="dialog" aria-modal="true" aria-labelledby="lifetime-title" data-testid="lifetime-modal">
          <motion.div initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
            className="relative w-full max-w-md rounded-2xl border border-[#c9a84c]/30 bg-[#0a1225] p-7 shadow-2xl">
            <button type="button" onClick={decline} disabled={closing || loading} aria-label="Cerrar"
              className="absolute top-4 right-4 text-gray-400 hover:text-white" data-testid="lifetime-close">
              <X size={18} />
            </button>
            <div className="w-12 h-12 rounded-full bg-[#c9a84c]/10 border border-[#c9a84c]/30 flex items-center justify-center mb-5">
              <InfinityIcon size={22} className="text-[#c9a84c]" />
            </div>
            <h2 id="lifetime-title" className="text-white text-xl font-semibold leading-snug mb-2">
              ¿Quieres hacer upgrade a vitalicio por {money(offer.price_cents, offer.currency)}?
            </h2>
            <p className="text-sm text-gray-400 leading-relaxed mb-5">
              Convierte tu membresía {PLAN_NAME[offer.plan] || ''} en plaza vitalicia con un único pago y olvídate de las renovaciones anuales.
            </p>
            <div className="flex items-start gap-2 rounded-xl border border-amber-500/25 bg-amber-500/[0.07] px-4 py-3 mb-6">
              <AlertTriangle size={15} className="text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-100/90 leading-relaxed">
                Si cierras este pop-up ya no podrás aprovechar esta oferta y el precio será de {money(offer.late_price_cents, offer.currency)}.
              </p>
            </div>
            <LockedChannelNote billing={membership?.billing} amount={offer.price_cents / 100} currency={offer.currency} className="-mt-3 mb-5" />
            <button type="button" onClick={() => goToCheckout(setLoading)} disabled={loading || closing}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#c9a84c] text-[#0a1225] font-semibold text-sm py-3.5 hover:bg-[#d8b85c] disabled:opacity-60"
              data-testid="lifetime-accept">
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              Sí, quiero mi plaza vitalicia por {money(offer.price_cents, offer.currency)}
            </button>
            <button type="button" onClick={decline} disabled={closing || loading}
              className="w-full mt-3 text-xs text-gray-400 hover:text-gray-300 py-2" data-testid="lifetime-decline">
              No, gracias
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function LifetimeUpgradeBanner() {
  const { membership } = useDashboard();
  const offer = membership?.lifetime_offer;
  const [loading, setLoading] = useState(false);
  if (!offer || offer.show_popup) return null;
  const until = fmtDate(offer.deadline);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl border border-[#c9a84c]/25 bg-[#c9a84c]/[0.05] px-5 py-4" data-testid="lifetime-banner">
      <div className="flex items-start gap-3">
        <InfinityIcon size={18} className="text-[#c9a84c] mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm text-white font-medium">Haz vitalicia tu membresía {PLAN_NAME[offer.plan] || ''} por {money(offer.price_cents, offer.currency)}</p>
          <p className="text-xs text-gray-400 mt-0.5">Pago único, sin renovaciones anuales.{until ? ` Disponible hasta el ${until}.` : ''}</p>
          <LockedChannelNote billing={membership?.billing} amount={offer.price_cents / 100} currency={offer.currency} className="mt-1" />
        </div>
      </div>
      <button type="button" onClick={() => goToCheckout(setLoading)} disabled={loading}
        className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#c9a84c]/40 px-4 py-2.5 text-sm text-[#c9a84c] hover:bg-[#c9a84c]/10 disabled:opacity-60 flex-shrink-0">
        {loading && <Loader2 size={14} className="animate-spin" />} Pasar a vitalicia
      </button>
    </div>
  );
}
